import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, {
  ACCESS_TOKEN_STORAGE_KEY,
  extractAccessTokenFromAuthResponse,
  extractUserFromAuthResponse,
  readJwtPayloadUnsafe,
  syncAuthHeadersFromStorage,
} from '@/api';

/**
 * Merge GET /account/me into a base user without letting `undefined` from `me` wipe fields
 * (e.g. role) that were already set on the login/register response.
 * @param {Record<string, unknown>|null} base
 * @param {unknown} me
 */
function mergeProfileWithMe(base, me) {
  if (!me || typeof me !== 'object' || Array.isArray(me)) return base;
  const out = { ...(base || {}) };
  for (const [k, v] of Object.entries(me)) {
    if (v !== undefined) out[k] = v;
  }
  if (!out.role && base && typeof base.role === 'string' && base.role) {
    out.role = base.role;
  }
  return Object.keys(out).length ? out : base;
}

/** Fill missing `role` / ids from signed access JWT (payload is readable client-side; API still validates). */
function enrichUserFromAccessToken(nextUser, token) {
  if (!token || typeof token !== 'string') return nextUser;
  const pl = readJwtPayloadUnsafe(token);
  if (!pl || typeof pl !== 'object') return nextUser;
  const out = { ...(nextUser || {}) };
  const role = typeof pl.role === 'string' && pl.role.trim() ? pl.role.trim() : null;
  if (role && !out.role) out.role = role;
  if (!out._id && pl.sub != null) out._id = String(pl.sub);
  if (!out.email && pl.email != null) out.email = String(pl.email);
  return Object.keys(out).length ? out : nextUser;
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (ctx == null) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};

function readStoredToken() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage?.getItem(ACCESS_TOKEN_STORAGE_KEY);
    if (typeof raw !== 'string') return null;
    const t = raw.trim();
    return t || null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = guest, object = authed
  const [accessToken, setAccessToken] = useState(() => readStoredToken());
  const [authInitialized, setAuthInitialized] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const token = readStoredToken();
      if (!token) {
        setAccessToken(null);
        setUser(null);
        syncAuthHeadersFromStorage();
        return;
      }
      setAccessToken(token);
      syncAuthHeadersFromStorage();
      const { data } = await api.get('/account/me', { timeout: 20000 });
      let u = data && typeof data === 'object' ? { ...data } : null;
      u = enrichUserFromAccessToken(u, token);
      setUser(u);
    } catch {
      try {
        window.localStorage?.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      setAccessToken(null);
      setUser(null);
      syncAuthHeadersFromStorage();
    } finally {
      setAuthInitialized(true);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/account/login', { email, password }, { timeout: 25000 });
    const token = extractAccessTokenFromAuthResponse(data);
    if (token) {
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
      setAccessToken(token);
      syncAuthHeadersFromStorage();
    }
    let nextUser = extractUserFromAuthResponse(data);
    if (token) {
      try {
        const { data: me } = await api.get('/account/me', { timeout: 20000 });
        nextUser = mergeProfileWithMe(nextUser, me);
      } catch {
        /* keep merged login body */
      }
    }
    nextUser = enrichUserFromAccessToken(nextUser, token);
    setUser(nextUser ?? null);
    return nextUser;
  };

  /** @param {{ email: string; password: string; name: string; company_name?: string; phone?: string; country?: string }} payload */
  const register = async (payload) => {
    const { data } = await api.post('/account/register', payload, { timeout: 25000 });
    const token = extractAccessTokenFromAuthResponse(data);
    if (token) {
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
      setAccessToken(token);
      syncAuthHeadersFromStorage();
    }
    let nextUser = extractUserFromAuthResponse(data);
    if (token) {
      try {
        const { data: me } = await api.get('/account/me', { timeout: 20000 });
        nextUser = mergeProfileWithMe(nextUser, me);
      } catch {
        /* self-service register is customer-only; /me may fail cross-subdomain — still allow app */
      }
    }
    nextUser = enrichUserFromAccessToken(nextUser, token);
    if (nextUser && !nextUser.role) {
      nextUser = { ...nextUser, role: 'customer' };
    }
    setUser(nextUser ?? null);
    return nextUser;
  };

  const logout = async () => {
    try {
      await api.post('/account/logout', null, { timeout: 15000 });
    } catch {
      /* ignore */
    }
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    syncAuthHeadersFromStorage();
    setAccessToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        authInitialized,
        setUser,
        login,
        register,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
